// Next Diamonds — Cart Stone Mirror (universal /cart/add.js interceptor)
//
// Patches window.fetch (and XMLHttpRequest.send for safety) to intercept
// EVERY POST to /cart/add.js — regardless of which script / form / button
// initiated it — and injects line-item properties for Shape, Color and
// Clarity from the product's `main_stone_values` metaobject.
//
// Why this exists:
//   The per-PDP `pdp-stone-mirror.js` only injects DOM hidden inputs into
//   forms that exist when it runs. That misses:
//     - Quick Buy modals (Symmetry hides our snippet via .not-in-quickbuy)
//     - Third-party "Add to Cart" buttons that build their own FormData
//     - Recently added sections / AJAX-injected product cards
//     - Any future flow that bypasses our PDP form
//   This patch sits at the HTTP layer, so it catches every path that goes
//   through the standard Shopify cart API.
//
// Safety:
//   - Idempotent: if Shape/Color/Clarity are already on the request, leaves
//     them alone (so PDP injection still wins when present).
//   - Strictly /cart/add.js only — never touches /cart/change.js,
//     /cart/update.js, /cart.js, or anything else.
//   - Wrapped in try/catch at every parsing/serialization boundary — if
//     anything goes wrong we pass the original request through unmodified.
//   - Skips the patch entirely if the request has no recognizable variant
//     ID or the variant isn't in our registry.
//   - Does NOT depend on cart-transform / price logic. Pure attribute add.

(function () {
  'use strict';

  // window.NDStoneFacts is populated by pdp-stone-mirror.js as it discovers
  // <script data-nd-stone-facts> tags. We also watch for new ones below so
  // Quick Buy modal injection populates the registry as it opens.
  window.NDStoneFacts = window.NDStoneFacts || {};

  var FACT_KEYS = ['_Shape', '_Color', '_Clarity', '_Cut'];

  // -----------------------------------------------------------------
  // Registry helpers
  // -----------------------------------------------------------------

  function lookupFacts(variantId, productId, productHandle) {
    if (variantId) {
      var byVariant = window.NDStoneFacts['variant:' + variantId];
      if (byVariant) return byVariant;
    }
    if (productId) {
      var byProduct = window.NDStoneFacts['product:' + productId];
      if (byProduct) return byProduct;
    }
    if (productHandle) {
      var byHandle = window.NDStoneFacts['handle:' + productHandle];
      if (byHandle) return byHandle;
    }
    return null;
  }

  function factsAreUseful(facts) {
    if (!facts) return false;
    return FACT_KEYS.some(function (k) { return facts[k]; });
  }

  // -----------------------------------------------------------------
  // Body-shape adapters — Shopify /cart/add.js accepts both classic
  // form-encoded (id=...&properties[Shape]=...) AND a JSON payload
  // ({ id, properties } or { items: [{id, properties}, ...] }).
  // We support both, plus FormData and URLSearchParams.
  // -----------------------------------------------------------------

  function getEntry(formLike, name) {
    if (!formLike || typeof formLike.get !== 'function') return null;
    try { return formLike.get(name); } catch (e) { return null; }
  }

  function hasProp(formLike, key) {
    var v = getEntry(formLike, 'properties[' + key + ']');
    return v != null && v !== '';
  }

  function setProp(formLike, key, value) {
    if (typeof formLike.set === 'function') {
      try { formLike.set('properties[' + key + ']', value); return true; }
      catch (e) { return false; }
    }
    if (typeof formLike.append === 'function') {
      try { formLike.append('properties[' + key + ']', value); return true; }
      catch (e) { return false; }
    }
    return false;
  }

  function augmentFormLike(formLike) {
    var variantId = getEntry(formLike, 'id');
    var facts = lookupFacts(variantId);
    if (!factsAreUseful(facts)) return false;
    var changed = false;
    FACT_KEYS.forEach(function (k) {
      if (facts[k] && !hasProp(formLike, k)) {
        if (setProp(formLike, k, facts[k])) changed = true;
      }
    });
    return changed;
  }

  function augmentJsonItem(item) {
    if (!item || typeof item !== 'object') return false;
    var facts = lookupFacts(item.id, item.product_id);
    if (!factsAreUseful(facts)) return false;
    item.properties = item.properties || {};
    var changed = false;
    FACT_KEYS.forEach(function (k) {
      if (facts[k] && !item.properties[k]) {
        item.properties[k] = facts[k];
        changed = true;
      }
    });
    return changed;
  }

  function augmentJsonBody(jsonStr) {
    var obj;
    try { obj = JSON.parse(jsonStr); } catch (e) { return jsonStr; }
    var changed = false;
    if (Array.isArray(obj.items)) {
      obj.items.forEach(function (it) { if (augmentJsonItem(it)) changed = true; });
    } else if (obj && typeof obj === 'object' && obj.id != null) {
      if (augmentJsonItem(obj)) changed = true;
    }
    if (!changed) return jsonStr;
    try { return JSON.stringify(obj); } catch (e) { return jsonStr; }
  }

  function augmentUrlSearchParams(usp) {
    var existing = new URLSearchParams(usp.toString());
    var changed = augmentFormLike(existing);
    return { changed: changed, params: existing };
  }

  // -----------------------------------------------------------------
  // URL matcher — only /cart/add.js (not /cart/change, /cart/update,
  // /cart.js, /cart/add.json, etc.). Be strict.
  // -----------------------------------------------------------------

  function isCartAddUrl(input) {
    if (!input) return false;
    var url = '';
    if (typeof input === 'string') url = input;
    else if (input.url) url = input.url;
    else if (typeof input.toString === 'function') url = input.toString();
    if (!url) return false;
    // Strip query/hash for matching
    var path = url.split('?')[0].split('#')[0];
    return /\/cart\/add(\.js)?$/.test(path);
  }

  // -----------------------------------------------------------------
  // fetch patch
  // -----------------------------------------------------------------

  if (window.fetch && !window.fetch.__ndStoneMirrorPatched) {
    var origFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      try {
        if (!isCartAddUrl(input)) return origFetch(input, init);

        // Case A: Request object
        if (typeof Request !== 'undefined' && input instanceof Request) {
          return augmentRequest(input).then(function (newReq) {
            return origFetch(newReq, init);
          }).catch(function () {
            return origFetch(input, init);
          });
        }

        // Case B: URL + init
        if (!init || !init.body) return origFetch(input, init);
        return augmentInitBody(init).then(function (newInit) {
          return origFetch(input, newInit);
        }).catch(function () {
          return origFetch(input, init);
        });
      } catch (e) {
        return origFetch(input, init);
      }
    };
    window.fetch.__ndStoneMirrorPatched = true;
  }

  // Returns a Promise that resolves to a new Request with the augmented body
  // (or the original Request if no change is needed).
  function augmentRequest(req) {
    var contentType = req.headers && req.headers.get && req.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') !== -1) {
      return req.clone().text().then(function (text) {
        var newText = augmentJsonBody(text);
        if (newText === text) return req;
        return new Request(req, { body: newText });
      });
    }
    if (contentType.indexOf('application/x-www-form-urlencoded') !== -1) {
      return req.clone().text().then(function (text) {
        var usp = new URLSearchParams(text);
        var result = augmentUrlSearchParams(usp);
        if (!result.changed) return req;
        return new Request(req, { body: result.params.toString() });
      });
    }
    if (contentType.indexOf('multipart/form-data') !== -1) {
      return req.clone().formData().then(function (fd) {
        var changed = augmentFormLike(fd);
        if (!changed) return req;
        return new Request(req, { body: fd });
      });
    }
    return Promise.resolve(req);
  }

  // Returns a Promise that resolves to a new init object with the augmented
  // body (or the original init if no change is needed).
  function augmentInitBody(init) {
    var body = init.body;
    var newInit = Object.assign({}, init);

    // FormData — used by Symmetry's product-form via main.js cart add.
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var changed = augmentFormLike(body);
      // FormData is mutated in place; init.body still references it. No copy
      // needed unless we changed nothing, in which case we still return init.
      return Promise.resolve(changed ? newInit : init);
    }
    // URLSearchParams
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      var changed2 = augmentFormLike(body);
      return Promise.resolve(changed2 ? newInit : init);
    }
    // String — could be JSON or x-www-form-urlencoded.
    if (typeof body === 'string') {
      var hdrs = init.headers || {};
      var ct = '';
      if (typeof Headers !== 'undefined' && hdrs instanceof Headers) {
        ct = hdrs.get('content-type') || '';
      } else if (hdrs && typeof hdrs === 'object') {
        ct = hdrs['content-type'] || hdrs['Content-Type'] || '';
      }
      if (ct.indexOf('application/json') !== -1 || /^[\s\[\{]/.test(body)) {
        var newBody = augmentJsonBody(body);
        if (newBody === body) return Promise.resolve(init);
        newInit.body = newBody;
        return Promise.resolve(newInit);
      }
      // x-www-form-urlencoded
      try {
        var usp2 = new URLSearchParams(body);
        var res = augmentUrlSearchParams(usp2);
        if (!res.changed) return Promise.resolve(init);
        newInit.body = res.params.toString();
        return Promise.resolve(newInit);
      } catch (e) {
        return Promise.resolve(init);
      }
    }
    return Promise.resolve(init);
  }

  // -----------------------------------------------------------------
  // XMLHttpRequest patch (safety net — most modern themes use fetch)
  // -----------------------------------------------------------------

  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.send.__ndStoneMirrorPatched) {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ndCartAdd = (String(method || '').toUpperCase() === 'POST') && isCartAddUrl(url);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (!this.__ndCartAdd || body == null) return origSend.apply(this, arguments);
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          augmentFormLike(body);
          return origSend.call(this, body);
        }
        if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
          augmentFormLike(body);
          return origSend.call(this, body);
        }
        if (typeof body === 'string') {
          var trimmed = body.trim();
          if (trimmed[0] === '{' || trimmed[0] === '[') {
            var newBody = augmentJsonBody(body);
            return origSend.call(this, newBody);
          }
          try {
            var usp = new URLSearchParams(body);
            var res = augmentUrlSearchParams(usp);
            if (res.changed) return origSend.call(this, res.params.toString());
          } catch (e) {}
        }
      } catch (e) {}
      return origSend.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send.__ndStoneMirrorPatched = true;
  }

  // -----------------------------------------------------------------
  // MutationObserver — Quick Buy modals inject the PDP markup
  // (including our <script data-nd-stone-facts>) AFTER pdp-stone-mirror.js
  // ran. Re-harvest whenever a new fact-script appears in the DOM.
  // -----------------------------------------------------------------

  function rescanFactScripts() {
    var nodes = document.querySelectorAll('script[data-nd-stone-facts]');
    nodes.forEach(function (node) {
      if (node.__ndHarvested) return;
      node.__ndHarvested = true;
      var data;
      try { data = JSON.parse((node.textContent || '').trim() || '{}'); }
      catch (e) { return; }
      if (!data) return;
      var facts = {
        _Shape: data.Shape || '',
        _Color: data.Color || '',
        _Clarity: data.Clarity || '',
        _Cut: data.Cut || ''
      };
      if (data.productHandle) window.NDStoneFacts['handle:' + data.productHandle] = facts;
      if (data.productId) window.NDStoneFacts['product:' + data.productId] = facts;
      if (Array.isArray(data.variantIds)) {
        data.variantIds.forEach(function (vid) {
          window.NDStoneFacts['variant:' + vid] = facts;
        });
      }
    });
  }

  // Initial harvest (in case this script runs after pdp-stone-mirror.js,
  // or if pdp-stone-mirror.js failed for any reason).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rescanFactScripts);
  } else {
    rescanFactScripts();
  }

  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(function (mutations) {
      var shouldRescan = false;
      for (var i = 0; i < mutations.length && !shouldRescan; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('script[data-nd-stone-facts]')) {
            shouldRescan = true; break;
          }
          if (node.querySelector && node.querySelector('script[data-nd-stone-facts]')) {
            shouldRescan = true; break;
          }
        }
      }
      if (shouldRescan) rescanFactScripts();
    });
    function startObserver() {
      mo.observe(document.body, { childList: true, subtree: true });
    }
    if (document.body) startObserver();
    else document.addEventListener('DOMContentLoaded', startObserver);
  }
})();
